// The settings forms' shared pieces (P-021): a setting's label with its (i) tooltip from the
// catalogue (lib/settings.ts), the label | control row the repos form is made of, an ordered list
// of commands, an on/off with its default, and name/value pairs (truth docs, numbered files).
import { useState, type ReactNode } from 'react';
import { setting, type Setting } from '../lib/settings';
import { Button, InfoTip, Input, Switch, cn, mono } from './ui';

/** A setting's tooltip: its description, YAML key and default. */
export function SettingTip({ s, yamlKey, align, label }: { s: Setting; yamlKey?: string; align?: 'start' | 'end'; label?: string }) {
  return (
    <InfoTip label={label ?? s.label} yamlKey={yamlKey ?? s.path} fallback={s.default} align={align}>
      {s.description}
    </InfoTip>
  );
}

/** The label, (i) and YAML key above a control (the machines form's developer, sub-card fields). */
export function SettingLabel({ path, yamlKey, label, htmlFor }: { path: string; yamlKey?: string; label?: string; htmlFor?: string }) {
  const s = setting(path);
  return (
    <span className="flex min-w-0 flex-wrap items-center gap-1.5">
      {htmlFor ? (
        <label htmlFor={htmlFor} className="text-[12.5px] font-semibold text-ink-display">
          {label ?? s.label}
        </label>
      ) : (
        <span className="text-[12.5px] font-semibold text-ink-display">{label ?? s.label}</span>
      )}
      <SettingTip s={s} yamlKey={yamlKey} label={label} />
    </span>
  );
}

/** One row of the repos form: label, (i) and YAML key on the left, the control on the right
 *  (one column on a phone). `reset` offers "Use default" for a key the file sets. */
export function SettingRow({
  path,
  yamlKey,
  label,
  extra,
  reset,
  children,
}: {
  path: string;
  yamlKey: string;
  label?: string;
  extra?: ReactNode;
  reset?: () => void;
  children: ReactNode;
}) {
  const s = setting(path);
  return (
    <div className="grid grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] items-center gap-x-[18px] gap-y-2 border-b border-hairline px-3.5 py-[11px] last:border-b-0 phone:grid-cols-1" data-setting={s.path}>
      <div className="flex min-w-0 flex-col gap-0.5">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[13px] font-semibold text-ink-display">{label ?? s.label}</span>
          <SettingTip s={s} yamlKey={yamlKey} label={label} />
          {extra}
        </div>
        <span className={cn(mono, 'text-[11px] text-ink-muted [overflow-wrap:anywhere]')}>{yamlKey}</span>
      </div>
      <div className="flex min-w-0 flex-wrap items-center gap-1.5">
        {children}
        {reset && (
          <Button size="sm" variant="ghost" onClick={reset}>
            Use default
          </Button>
        )}
      </div>
    </div>
  );
}

/** On or off, showing meta-kit's default (greyed "· default") while the key is unset. */
export function Toggle({ label, value, fallback, onChange }: { label: string; value: unknown; fallback: boolean; onChange: (on: boolean) => void }) {
  const set = typeof value === 'boolean';
  const on = set ? value : fallback;
  return (
    <>
      <Switch checked={on} aria-label={label} onChange={onChange} />
      <span className="text-[12px] text-ink-muted">
        {on ? 'On' : 'Off'}
        {!set && ' · default'}
      </span>
    </>
  );
}

/** Commands in order, one field each (an empty field is dropped when saved). */
export function CommandList({ label, value, onChange, placeholder }: { label: string; value: string[]; onChange: (v: string[]) => void; placeholder?: string }) {
  const [rows, setRows] = useState<string[]>(value);
  const update = (next: string[]) => {
    setRows(next);
    onChange(next.map((s) => s.trim()).filter(Boolean));
  };
  return (
    <div className="grid w-full gap-1.5">
      {rows.map((cmd, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <Input aria-label={`${label} ${i + 1}`} className="h-[30px]" value={cmd} onChange={(e) => update(rows.map((r, j) => (j === i ? e.target.value : r)))} />
          <Button size="icon" variant="ghost" aria-label={`Remove ${label} ${i + 1}`} onClick={() => update(rows.filter((_, j) => j !== i))}>
            ×
          </Button>
        </div>
      ))}
      {!rows.length && <span className="text-[12px] text-ink-muted">{placeholder ?? 'None'}</span>}
      <Button size="sm" variant="ghost" className="justify-self-start border-dashed border-hairline-strong" onClick={() => update([...rows, ''])}>
        + add command
      </Button>
    </div>
  );
}

/** Name → value pairs of a mapping (truth docs), each removable; a new one is named first. */
export function PairList({
  label,
  names,
  value,
  onChange,
  onRemove,
  onAdd,
  valueLabel = 'Path',
}: {
  label: string;
  names: string[];
  value: (name: string) => string;
  onChange: (name: string, v: string) => void;
  onRemove: (name: string) => void;
  onAdd: (name: string) => void;
  valueLabel?: string;
}) {
  return (
    <div className="grid w-full gap-1.5">
      {names.map((name) => (
        <div key={name} className="grid grid-cols-[minmax(0,0.7fr)_minmax(0,1.3fr)_auto] items-center gap-1.5">
          <code className={cn(mono, 'truncate text-ink-display')}>{name}</code>
          <Input aria-label={`${label} · ${name}`} className="h-[30px]" value={value(name)} onChange={(e) => onChange(name, e.target.value)} />
          <Button size="icon" variant="ghost" aria-label={`Remove ${name} from ${label}`} onClick={() => onRemove(name)}>
            ×
          </Button>
        </div>
      ))}
      <AddName label={label} placeholder={`name, then its ${valueLabel.toLowerCase()}`} taken={names} onAdd={onAdd} />
    </div>
  );
}

/** A field and button that add a named entry (a code repo, component, truth doc). */
export function AddName({ label, taken, onAdd, placeholder }: { label: string; taken: string[]; onAdd: (name: string) => void; placeholder?: string }) {
  const [name, setName] = useState('');
  const clean = name.trim();
  const ok = clean !== '' && !taken.includes(clean);
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Input aria-label={`New ${label} name`} className="h-[30px] max-w-[220px]" value={name} placeholder={placeholder ?? 'name'} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => {
        if (e.key === 'Enter' && ok) {
          e.preventDefault();
          onAdd(clean);
          setName('');
        }
      }} />
      <Button size="sm" disabled={!ok} onClick={() => {
        onAdd(clean);
        setName('');
      }}>
        + Add {label}
      </Button>
    </div>
  );
}
